// /app/frontend/src/pages/FeedbackPage.jsx
// Customer feedback page for post-job surveys

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Star, Check, Loader2, ThumbsUp, ThumbsDown, Home } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FeedbackPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [error, setError] = useState(null);
  
  // Form state
  const [rating, setRating] = useState(parseInt(searchParams.get('rating')) || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);

  useEffect(() => {
    fetchFeedbackInfo();
  }, [token]);

  const fetchFeedbackInfo = async () => {
    try {
      const response = await axios.get(`${API}/api/feedback/${token}`);
      setFeedbackData(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This feedback link has expired or has already been submitted.');
      } else {
        setError('Unable to load feedback form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast.error('Please select a star rating');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/feedback/${token}`, {
        rating,
        comment,
        would_recommend: wouldRecommend
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Feedback Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">
              <Home className="h-4 w-4 mr-2" />
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-2">
              Your feedback has been submitted successfully.
            </p>
            <div className="flex justify-center gap-1 my-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-8 w-8 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                />
              ))}
            </div>
            <p className="text-sm text-gray-500 mb-6">
              We appreciate you taking the time to share your experience with us.
            </p>
            <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">
              <Home className="h-4 w-4 mr-2" />
              Visit Apollo Shades
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Apollo Shades</h1>
          <p className="text-gray-500">Customer Feedback</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>How was your experience?</CardTitle>
            <CardDescription>
              {feedbackData?.customer_name && `Hi ${feedbackData.customer_name}, `}
              We'd love to hear about your recent{' '}
              {feedbackData?.event_type?.replace('_', ' ') || 'service'} experience
              {feedbackData?.installer_name && ` with ${feedbackData.installer_name}`}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Overall Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`rating-star-${star}`}
                    >
                      <Star
                        className={`h-10 w-10 transition-colors ${
                          star <= (hoverRating || rating)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="text-center mt-2 text-sm text-gray-600">
                    {rating === 5 ? 'Excellent!' : 
                     rating === 4 ? 'Great!' :
                     rating === 3 ? 'Good' :
                     rating === 2 ? 'Fair' : 'Poor'}
                  </p>
                )}
              </div>

              {/* Would Recommend */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Would you recommend Apollo Shades to others?
                </label>
                <div className="flex gap-3 justify-center">
                  <Button
                    type="button"
                    variant={wouldRecommend === true ? 'default' : 'outline'}
                    onClick={() => setWouldRecommend(true)}
                    className={wouldRecommend === true ? 'bg-green-600 hover:bg-green-700' : ''}
                    data-testid="recommend-yes"
                  >
                    <ThumbsUp className="h-4 w-4 mr-2" />
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={wouldRecommend === false ? 'default' : 'outline'}
                    onClick={() => setWouldRecommend(false)}
                    className={wouldRecommend === false ? 'bg-red-600 hover:bg-red-700' : ''}
                    data-testid="recommend-no"
                  >
                    <ThumbsDown className="h-4 w-4 mr-2" />
                    No
                  </Button>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Additional Comments (Optional)
                </label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us more about your experience..."
                  rows={4}
                  className="resize-none"
                  data-testid="feedback-comment"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={submitting || rating === 0}
                data-testid="submit-feedback-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Apollo Shades | (212) 209-0654 | info@apolloshades.com
        </p>
      </div>
    </div>
  );
}
